import React, { useState } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SELECT_CLASSES } from "@/lib/styles";
import {
  FIRMNESS_OPTIONS,
  groupCategories,
  orgLabel,
} from "@/pages/cashflow/utils";
import type { CashflowCategory, CashflowOrg } from "@/types/api";

interface FieldProps {
  id: string;
  field: UseFormRegisterReturn;
  error?: string | undefined;
}

export function FieldError({ message }: { message?: string | undefined }): React.ReactElement | null {
  if (!message) return null;
  return <p className="text-xs text-rose-600">{message}</p>;
}

/**
 * Постоянная панель ошибки (не тост): текст `detail` с бэка виден, пока ошибка
 * не устранена, а не исчезает через пару секунд.
 */
export function ErrorPanel({
  detail,
  children,
}: {
  detail: string;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
      <p>{detail}</p>
      {children}
    </div>
  );
}

export function OrgField({
  id,
  field,
  error,
  orgInns,
  orgs,
}: FieldProps & { orgInns: string[]; orgs: CashflowOrg[] }): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Организация</Label>
      <select id={id} className={SELECT_CLASSES} {...field}>
        <option value="">— выберите —</option>
        {orgInns.map((inn) => (
          <option key={inn} value={inn}>
            {orgLabel(inn, orgs)}
          </option>
        ))}
      </select>
      <FieldError message={error} />
    </div>
  );
}

export function CategoryField({
  id,
  field,
  error,
  categories,
}: FieldProps & { categories: CashflowCategory[] }): React.ReactElement {
  const grouped = groupCategories(categories);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Статья</Label>
      <select id={id} className={SELECT_CLASSES} {...field}>
        <option value="">— выберите —</option>
        <optgroup label="Приход">
          {grouped.in.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Расход">
          {grouped.out.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </optgroup>
      </select>
      <FieldError message={error} />
    </div>
  );
}

export function FirmnessField({ id, field, error }: FieldProps): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Твёрдость</Label>
      <select id={id} className={SELECT_CLASSES} {...field}>
        {FIRMNESS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <FieldError message={error} />
    </div>
  );
}

/**
 * Удаление в два клика без `window.confirm`: первый клик переводит кнопку
 * в состояние «Точно?», второй — удаляет.
 */
export function ConfirmDeleteButton({
  onConfirm,
  disabled = false,
}: {
  onConfirm: () => void;
  disabled?: boolean;
}): React.ReactElement {
  const [armed, setArmed] = useState(false);
  return (
    <Button
      variant={armed ? "destructive" : "ghost"}
      size="sm"
      disabled={disabled}
      onClick={() => {
        if (armed) {
          onConfirm();
          setArmed(false);
        } else {
          setArmed(true);
        }
      }}
      onMouseLeave={() => setArmed(false)}
    >
      {armed ? "Точно?" : "Удалить"}
    </Button>
  );
}
